import {
  activateDrawer,
  lockScroll,
  unlockScroll,
  bindDrawerEscape,
  afterDrawerClose,
} from '@theme/coco-utilities';

const DEFAULT_DRAWER_ID = 'hamburgerDrawer';
const PROMO_OPEN_DELAY_MS = 1000;
const PROMO_TRANSITION_MS = 500;

class MenuDrawer extends HTMLButtonElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onDrawerClick = this.onDrawerClick.bind(this);
    this._promoOpenTimer = null;
    this._closing = false;
  }

  connectedCallback() {
    if (!this.getAttribute('aria-controls')) {
      this.setAttribute('aria-controls', DEFAULT_DRAWER_ID);
    }
    this.setAttribute('aria-haspopup', 'dialog');
    this.addEventListener('click', this.onClick);
    this.bindDrawer();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.clearPromoTimer();
  }

  get drawerId() {
    return this.getAttribute('aria-controls') || DEFAULT_DRAWER_ID;
  }

  get drawer() {
    return document.getElementById(this.drawerId);
  }

  get promoMedia() {
    return this.drawer?.querySelector('[data-hamburger-promo]');
  }

  clearPromoTimer() {
    if (this._promoOpenTimer) {
      clearTimeout(this._promoOpenTimer);
    }
    this._promoOpenTimer = null;
  }

  openPromoMedia() {
    this.clearPromoTimer();
    this.promoMedia?.classList.remove('open');

    this._promoOpenTimer = setTimeout(() => {
      this._promoOpenTimer = null;
      this.promoMedia?.classList.add('open');
    }, PROMO_OPEN_DELAY_MS);
  }

  closePromoMedia() {
    return new Promise((resolve) => {
      this.clearPromoTimer();

      const promo = this.promoMedia;
      if (!promo?.classList.contains('open')) {
        resolve();
        return;
      }

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        promo.removeEventListener('transitionend', onEnd);
        resolve();
      };

      const onEnd = (event) => {
        if (event.target !== promo || event.propertyName !== 'clip-path') return;
        finish();
      };

      promo.addEventListener('transitionend', onEnd);
      promo.classList.remove('open');
      setTimeout(finish, PROMO_TRANSITION_MS + 50);
    });
  }

  syncTriggers(drawer, expanded) {
    if (!drawer?.id) return;

    document.querySelectorAll(`button[aria-controls="${drawer.id}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  bindDrawer() {
    const drawer = this.drawer;
    if (!drawer || drawer.dataset.menuDrawerBound) return;

    drawer.dataset.menuDrawerBound = 'true';
    drawer.addEventListener('click', this.onDrawerClick);
    drawer._unbindEscape = bindDrawerEscape(drawer, () => this.close(drawer));

    const syncTriggers = () => {
      if (drawer.hasAttribute('active')) return;
      this.syncTriggers(drawer, false);
    };

    new MutationObserver(syncTriggers).observe(drawer, {
      attributes: true,
      attributeFilter: ['active'],
    });
  }

  onClick(event) {
    event.preventDefault();

    const drawer = this.drawer;
    if (!drawer) return;

    if (drawer.hasAttribute('active') || this._closing) {
      this.close(drawer);
      return;
    }

    this.open(drawer);
  }

  onDrawerClick(event) {
    const drawer = this.drawer;
    if (!drawer?.hasAttribute('active') || this._closing) return;

    const inner = drawer.querySelector('[data-drawer-inner]');
    const isInsidePanel = inner?.contains(event.target);

    if (event.target.closest('[is="close-drawer"]') || !isInsidePanel) {
      event.preventDefault();
      this.close(drawer);
    }
  }

  open(drawer) {
    if (!drawer || drawer.hasAttribute('active') || this._closing) return;

    // Reveal before animating so focusable links stay out of tab order while closed
    // (uses [hidden], not inert).
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('open', '');
    drawer.setAttribute('hamburger_open', '');
    requestAnimationFrame(() => drawer.setAttribute('active', ''));
    lockScroll();

    this.syncTriggers(drawer, true);
    this.openPromoMedia();

    drawer.releaseModal?.();
    drawer.releaseModal = activateDrawer(drawer, {
      initialFocus: (el) =>
        el.querySelector('[is="close-drawer"]:not([data-fixed-overlay])') || el,
    });
  }

  async close(drawer = this.drawer) {
    if (!drawer?.hasAttribute('active') || this._closing) return;

    this._closing = true;

    // Clip-path close first, then drawer
    await this.closePromoMedia();

    drawer.removeAttribute('active');
    this.syncTriggers(drawer, false);

    drawer.releaseModal?.(this);
    drawer.releaseModal = null;

    afterDrawerClose(drawer, () => {
      if (!drawer.hasAttribute('active')) {
        drawer.removeAttribute('open');
        drawer.setAttribute('hidden', '');
        drawer.setAttribute('aria-hidden', 'true');

        unlockScroll();
        setTimeout(() => {
          drawer.removeAttribute('hamburger_open');
        }, 650);
      }
      this._closing = false;
    });
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer, { extends: 'button' });
}

class MobileMenuDrawer extends MenuDrawer {}

if (!customElements.get('mobile-menu-drawer')) {
  customElements.define('mobile-menu-drawer', MobileMenuDrawer, { extends: 'button' });
}
